import React, { useState } from "react";
import { motion } from "motion/react";
import { ArrowLeft, CreditCard, Truck, Check } from "lucide-react";
import { useStore } from "@/src/contexts/StoreContext";
import { cn } from "@/src/lib/utils";

interface CheckoutFormProps {
  onBack: () => void;
  onComplete: () => void;
}

export function CheckoutForm({ onBack, onComplete }: CheckoutFormProps) {
  const { cart, getCartTotal, formatPrice, createOrder, completePayment } = useStore();
  const [step, setStep] = useState<"shipping" | "payment" | "processing" | "success">("shipping");
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");

  const [shipping, setShipping] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });

  const subtotal = getCartTotal();
  const shippingCost = subtotal > 5000 ? 0 : 999;
  const tax = Math.round(subtotal * 0.08);
  const total = subtotal + shippingCost + tax;

  const handleShippingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep("payment");
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setStep("processing");

    // Create order
    const order = await createOrder(shipping);
    
    if (order) {
      setOrderNumber(order.orderNumber);
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Complete payment
      const success = await completePayment(order.orderId);
      
      if (success) {
        setStep("success");
        setTimeout(onComplete, 3000);
      } else {
        setStep("payment");
        setIsProcessing(false);
      }
    } else {
      setStep("payment");
      setIsProcessing(false);
    }
  };

  if (step === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Order Confirmed!</h2>
        <p className="text-white/60 mb-4">Thank you for your purchase</p>
        <p className="text-lg font-mono text-white/80 bg-white/5 px-4 py-2 rounded-lg inline-block">
          Order: {orderNumber}
        </p>
        <p className="text-sm text-white/40 mt-6">Redirecting to your orders...</p>
      </motion.div>
    );
  }

  if (step === "processing") {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 border-4 border-white/10 border-t-blue-500 rounded-full animate-spin mx-auto mb-6" />
        <h2 className="text-xl font-semibold text-white mb-2">Processing Payment...</h2>
        <p className="text-white/60">Please don't close this window</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-white/40 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold text-white">Checkout</h2>
      </div>

      {/* Progress */}
      <div className="flex gap-2">
        <div className={cn("flex-1 h-1 rounded-full", step === "shipping" ? "bg-blue-500" : "bg-green-500")} />
        <div className={cn("flex-1 h-1 rounded-full", step === "payment" ? "bg-blue-500" : "bg-white/10")} />
      </div>

      {/* Shipping Form */}
      {step === "shipping" && (
        <motion.form
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          onSubmit={handleShippingSubmit}
          className="space-y-4"
        >
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-5 h-5 text-blue-400" />
            <h3 className="font-semibold text-white">Shipping Information</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-white/60 mb-1">Full Name *</label>
              <input
                required
                type="text"
                value={shipping.name}
                onChange={(e) => setShipping({ ...shipping, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="John Doe"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-white/60 mb-1">Email *</label>
              <input
                required
                type="email"
                value={shipping.email}
                onChange={(e) => setShipping({ ...shipping, email: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Phone</label>
              <input
                type="tel"
                value={shipping.phone}
                onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="+1 234 567 890"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Country *</label>
              <select
                required
                value={shipping.country}
                onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="UK">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="ID">Indonesia</option>
                <option value="JP">Japan</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-white/60 mb-1">Address *</label>
              <input
                required
                type="text"
                value={shipping.address}
                onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="123 Main Street"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">City *</label>
              <input
                required
                type="text"
                value={shipping.city}
                onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="New York"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">State/Province</label>
              <input
                type="text"
                value={shipping.state}
                onChange={(e) => setShipping({ ...shipping, state: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="NY"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Postal Code *</label>
              <input
                required
                type="text"
                value={shipping.postal_code}
                onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                placeholder="10001"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all mt-6"
          >
            Continue to Payment
          </button>
        </motion.form>
      )}

      {/* Payment Form */}
      {step === "payment" && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-4"
        >
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-blue-400" />
            <h3 className="font-semibold text-white">Payment Method</h3>
          </div>

          {/* Order Summary */}
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-2">
            <h4 className="text-sm font-medium text-white/60 mb-3">Order Summary</h4>
            {cart.map((item) => {
              const price = item.product.base_price + item.variant.price_modifier;
              return (
                <div key={`${item.product.id}-${item.variant.id}`} className="flex justify-between text-sm">
                  <span className="text-white/60">
                    {item.product.name} ({item.variant.variant_name}) x{item.quantity}
                  </span>
                  <span className="text-white">{formatPrice(price * item.quantity)}</span>
                </div>
              );
            })}
            <div className="border-t border-white/5 pt-2 mt-2 space-y-1">
              <div className="flex justify-between text-sm text-white/60">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-white/60">
                <span>Shipping</span>
                <span>{shippingCost === 0 ? "Free" : formatPrice(shippingCost)}</span>
              </div>
              <div className="flex justify-between text-sm text-white/60">
                <span>Tax</span>
                <span>{formatPrice(tax)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold text-white pt-1">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>
          </div>

          {/* Mock Payment Options */}
          <div className="space-y-3">
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Pay {formatPrice(total)}
            </button>
            <p className="text-xs text-white/40 text-center">
              Demo Mode: No actual payment will be charged
            </p>
          </div>

          <button
            onClick={() => setStep("shipping")}
            className="w-full py-3 text-white/40 text-sm hover:text-white transition-colors"
          >
            Back to Shipping
          </button>
        </motion.div>
      )}
    </div>
  );
}